import React from 'react';
import { Box, Flex, Text, TextLink } from '@ascension/web';
import { useDispatch, useSelector } from 'react-redux';
import { clearScrollPos, getCaseListData } from '@store/actions/CaseListActionCreators';
import { RootState } from '@interfaces/rootState';
import CaseCard from '@components/CaseTrackerList/CaseCard/CaseCard';
import { CaseTrackerSection } from '@components/CaseTrackerSection/CaseTrackerSection';
import SortingComponent from '@components/SortingComponent/SortingComponent';
import { useHistory } from 'react-router-dom';
import ProcedureDateFilter from '@components/Filters/ProcedureDate/ProcedureDateFilter';
import CaseListOtherFilters from '@components/Filters/CaseListOtherFilters/CaseListOtherFilters';
import { PreferredSurgeonFilter } from '@components/Filters/SurgeonFilter/SurgeonFilter';
import ListHeaderComponent from '@components/ListHeader/ListHeaderComponent';
import { DEFAULT_CASES_SORT } from '@utilities/apiConstants';
import { CaseTrackerListInterface } from '@components/CaseTrackerList/CaseTrackerListInterface';
import PaginationComponent from '@components/Pagination/PaginationComponent';
import TaskSection from '@components/CaseTrackerList/TaskSection/TaskSection';
import {
	FiltersProvider,
	useResetAllFilters,
	useFilterState,
} from '@components/Filters/FiltersContext/FiltersContext';
import { FilterApiParams } from '@interfaces/FilterParams';
import usePageEffects from '@utilities/hooks/usePageEffects/usePageEffects';
import { CaseListCountSelector, CaseListFilterSelector } from '@store/selectors/CaseListSelector';
import { makeStyles } from '@material-ui/core/styles';
import { filtersToReplaceString } from '@utilities/commonFunction';
import { CASES_ENTITY_NAME, filterSortOptions, QUEUE_BASE_ROUTE } from '@utilities/constants';
import SavedFilterSwitch from '@components/SavedFilterSwitch/SavedFilterSwitch';
import { MyFiltersSelector } from '@store/selectors/MyFiltersSelector';
import { savedFiltersToApiParams } from '@utilities/filtersUtilities';
import { MyFiltersState } from '@interfaces/MyFilters/MyFiltersState';
import {
	UserProfileFilterUrlSelector,
	UserProfileHasARoleSelector
} from '@store/selectors/UserProfileSelector';
import { HospitalInteractiveRoles, OfficeRoles } from '@interfaces/UserProfile/UserRoleEnum';
import { StorageKey } from '@utilities/authConstants';
import { saveScrollPos } from '@store/actions/CaseListActionCreators';
import { useAppPageBodyClassSetter } from '@components/AppPage/AppPageContext/AppPageContext';
import { maxWidthContainerStyle } from '@theme/useMaxWidthContainerStyle';
import useSurgeonsFilterUpdate from '@utilities/hooks/useSurgeonsFilterUpdate/useSurgeonsFilterUpdate';
import getInitialFilters from '@components/CaseTrackerList/utilities/getInitialFilters/getInitialFilters';
import {
	useHomeNavigationContext
} from '@components/HeaderSection/NavigationOptions/HomeNavigationContext/HomeNavigationContext';
import BoxWithLoader from '@components/Loaders/BoxWithLoader/BoxWithLoader';
import { GET_CASE_LIST } from '@store/actions/types';
import { BlockReleaseReviewBanner } from '@components/CaseTrackerList/BlockReleaseReviewBanner/BlockReleaseReviewBanner';
import { BlockNudgeBanner } from '@components/BlockRelease/BlockNudgeBanner/BlockNudgeBanner';
import useHasPermission from '@utilities/hooks/useHasPermission/useHasPermission';
import { PermittedAction } from '@utilities/PermittedAction';
import { useSsmUiFlags } from '@utilities/hooks/useSsmUiFlags/useSsmUiFlags';
import { ScrollPosition } from '@interfaces/CaseListTypes';

const useStyles = makeStyles({
	listHeader: {
		fontSize: '14px',
		borderTop: '1px solid #C9C9C9',
	},
	filterSwitchText: {
		alignSelf: 'center'
	},
	appBody: {
		'& > div': maxWidthContainerStyle(),
	},
	loader: {
		zIndex: 1,
	},
});

const ListEmpty = () => {
	const savedFilters = useSelector(MyFiltersSelector);
	const { filterApplied, toggleFilter } = useFilterState();
	const resetFilters = useResetAllFilters();
	const isOfficeRole = useSelector(UserProfileHasARoleSelector(OfficeRoles));
	const savedFilterUrl = useSelector(UserProfileFilterUrlSelector);
	const { updateSurgeonsWithIds } = useSurgeonsFilterUpdate();

	const handleResetFilters = React.useCallback(() => {
		const savedFiltersByRole: MyFiltersState = isOfficeRole ?
			{ ...savedFilters, surgeon: [] } :
			{ ...savedFilters, unit: [] };
		updateSurgeonsWithIds([]);
		!!savedFilterUrl && toggleFilter(true);
		resetFilters(savedFiltersToApiParams(savedFiltersByRole));
	}, [isOfficeRole, resetFilters, savedFilterUrl, savedFilters, toggleFilter, updateSurgeonsWithIds]);

	return (
		<Flex mt={5} justifyContent="center" alignItems="center" flexDirection="column" data-field="Reset_Filters">
			<Text fontSize={12} color="#727272" lineHeight={1.25} display="block">Try editing your selected filters to see more cases, or reset your filters to see all cases. </Text>
			{!filterApplied && <TextLink mt={5} onMouseDown={handleResetFilters} id="resetFilters">CLEAR FILTERS</TextLink>}
		</Flex>
	);
};

const ListInner = () => {
	const dispatch = useDispatch();
	const { page: currentPaginationPage, updateFilterProps } = useFilterState();
	const updateScrollPos = React.useCallback((position: ScrollPosition) => {
		dispatch(saveScrollPos(position));
	}, [dispatch]);
	const classes = useStyles();
	const { updateSurgeonsWithIds } = useSurgeonsFilterUpdate();

	const { ssmShowReleaseBlock20241205 } = useSsmUiFlags();
	const hasHospitalRole = useSelector(UserProfileHasARoleSelector(HospitalInteractiveRoles));
	const shouldShowReleasesReviewBanner = hasHospitalRole && ssmShowReleaseBlock20241205;

	const { setBodyClass } = useAppPageBodyClassSetter('CaseList');

	React.useEffect(() => {
		setBodyClass(classes.appBody);
	}, [classes.appBody, setBodyClass]);

	const { caseList: caseDetails, error, scrollPos } = useSelector((state: RootState) => state.caseList);

	usePageEffects('Case List');
	const history = useHistory();

	const { filters } = useFilterState();
	const filtersString = filtersToReplaceString(filters, '');
	const pushAndSetItem = (link: string) => {
		sessionStorage.setItem(StorageKey.QUEUE_QUERY, link);
		history.push(QUEUE_BASE_ROUTE);
	};

	React.useEffect(() => {
		// Exit if scrollPos is not defined
		if (!scrollPos?.page) return;

		// Ensure we are on the correct page
		if (currentPaginationPage && currentPaginationPage !== scrollPos.page) {
			updateFilterProps({ page: scrollPos.page });
			return;
		}

		// Ensure the case exists in the current page
		if (!caseDetails.find(item => item.id === scrollPos.id)) return;

		// Wait until the element is present in the DOM and scroll to it
		const timeout = setTimeout(() => {
			const scrollTo = document.getElementById(scrollPos.id);
			if (scrollTo) {
				scrollTo.scrollIntoView({ block: 'center' });
				dispatch(clearScrollPos()); // Clear scroll position after scrolling
			}
		}, 0);

		// Cleanup timeout on unmount
		return () => clearTimeout(timeout);
	}, [caseDetails, currentPaginationPage, dispatch, scrollPos, updateFilterProps]);

	const hasPermission = useHasPermission(PermittedAction.VIEW_NAVIGATION_OPTIONS);

	return (
		<Box>
			{hasPermission &&
				<Box mt={15}>
					<BlockNudgeBanner />
				</Box>}
			{shouldShowReleasesReviewBanner && <BlockReleaseReviewBanner />}
			<Flex pt={40} mb={18} justifyContent="center" alignItems="center">
				<TaskSection/>
			</Flex>
			<CaseTrackerSection sectionTitle="Cases" width={1}>
				<Flex width={1} justifyContent="stretch" className="container standard-gap" minHeight={80}>
					<Box flex="2 2 17%" pr="10px">
						<SortingComponent options={filterSortOptions}/>
					</Box>
					<Box flex="4 4 33%" pr="10px">
						<PreferredSurgeonFilter onSurgeonFilterUpdate={updateSurgeonsWithIds} />
					</Box>
					<Box flex="3 3 25%" pr="10px">
						<CaseListOtherFilters />
					</Box>
					<Box flex="3 3 25%">
						<ProcedureDateFilter />
					</Box>
				</Flex>
				<Flex>
					<SavedFilterSwitch/>
					<Text className={classes.filterSwitchText}>My Filters</Text>
				</Flex>
			</CaseTrackerSection>
			{(caseDetails.length > 0) &&
				<Box data-field="CaseListHeader" pt={10} >
					<ListHeaderComponent className={classes.listHeader}>
						<TextLink mt={1} data-field="reviewCasesPage" onClick={() => pushAndSetItem(filtersString)}>
							REVIEW SEARCHED CASES &nbsp; <span className="angle-right">{'>'}</span>
						</TextLink>
					</ListHeaderComponent>
				</Box>
			}
			<Box position="relative" height="100%">
				<BoxWithLoader label={GET_CASE_LIST} loaderProps={{ className: classes.loader }}>
					{error || caseDetails.map((item: CaseTrackerListInterface, index: number) => (
						<Box data-field={'caseCardDetails' + item.id} key={index} id={item.id}>
							<CaseCard caseDetails={item}  onClick={() => {
								updateScrollPos({ id: item.id, page: currentPaginationPage });
								history.push('/cases/' + item.id);
							}}/>
						</Box>
					))}
					{(caseDetails.length > 0) &&
						<Box data-field="Pagination" pt={20} pb={30} >
							<PaginationComponent displayPerPage={true} fontSize="14px"/>
						</Box>
					}
					{(caseDetails.length == 0) &&
						<>
							<Flex mt={11} justifyContent="center" alignItems="center">
								<Text fontSize={18} display="block" white-space="pre-line">Sorry, this search didn&apos;t produce any results.</Text>
							</Flex>
							<ListEmpty />
						</>
					}
				</BoxWithLoader>
			</Box>
		</Box>
	);
};

export default function CaseTrackerList() {
	const dispatch = useDispatch();
	const currentFilters = useSelector(CaseListFilterSelector);
	const { filterMappedIdList } = useSurgeonsFilterUpdate();
	const initialFilterRef = React.useRef<FilterApiParams | undefined>(getInitialFilters(currentFilters, filterMappedIdList));
	const [shouldDisableDefaultFilters] = React.useState(!!filterMappedIdList);
	const { setHomeRoute } = useHomeNavigationContext();

	const loadListHandler = React.useCallback((urlParams: FilterApiParams) => {
		dispatch(getCaseListData(urlParams));
	}, [dispatch]);

	React.useEffect(() => {
		setHomeRoute('/caselist');
	}, [setHomeRoute]);

	return (
		<FiltersProvider
			entityName={CASES_ENTITY_NAME}
			totalCountSelector={CaseListCountSelector}
			defaultSort={DEFAULT_CASES_SORT}
			initialFilterRef={initialFilterRef}
			onListLoadRequest={loadListHandler}
			disableDefaultFilters={shouldDisableDefaultFilters}
		>
			<ListInner />
		</FiltersProvider>
	);
}
